import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    MeasuringPointLxnValuesDocument,
    SwarmType,
    type MeasuringPointLxnValuesQuery,
    type MeasuringPointLxnValuesQueryVariables,
} from '@/graphql/types';
import { DATA_TYPE } from '../../../../../../graph/enums';
import { schema } from '../../../../../../schema';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import type { RenderContext } from '../../../elements/types';
import {
    buildRequiredMeasuringPointWindowInput,
    toSoundMeasuringPointRelayId,
} from '../measuringPointInput';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeBindingInputArgs,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularColumnDescriptor,
} from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNullableFiniteNumber,
    normalizeRequiredFiniteNumber,
    normalizeRequiredId,
} from '../validationPrimitives';

export const SUPPORTED_LXN_PERCENTILES = schema.lnLevels;

type SupportedLxnPercentile = (typeof SUPPORTED_LXN_PERCENTILES)[number];
type LxnValueEntry = RuntimeDataServiceOutput<'measuring_point.lxn_values'>['values'][number];
type LxnInputBuilderArgs = RuntimeBindingInputArgs;

/**
 * The LXN service is the only caller, so this guard lives here rather than in a shared module.
 */
function requireSoundMeasuringPointContext(context: RenderContext, label: string) {
    if (context.kind !== 'measuring-point') {
        throw new Error(`Missing measuring-point context for ${label}.`);
    }

    if (context.measuringPoint.swarmType !== SwarmType.Sound) {
        throw new Error(`${label} require sound measuring-point context.`);
    }

    return context.measuringPoint;
}

function toLxnColumnKey(percentile: SupportedLxnPercentile): `l${SupportedLxnPercentile}` {
    return `l${percentile}`;
}

function validateLxnPercentiles(value: unknown, pathLabel: string): SupportedLxnPercentile[] {
    if (!Array.isArray(value)) {
        throw new Error(`Expected "${pathLabel}" to be an array.`);
    }

    if (value.length === 0) {
        throw new Error(`Expected "${pathLabel}" to contain at least one percentile.`);
    }

    const supportedPercentiles = new Set<number>(SUPPORTED_LXN_PERCENTILES);
    const seenPercentiles = new Set<number>();
    return value.map((percentile, index) => {
        const percentilePath = `${pathLabel}[${index}]`;
        if (typeof percentile !== 'number' || !Number.isInteger(percentile)) {
            throw new Error(`Expected "${percentilePath}" to be an integer.`);
        }

        if (!supportedPercentiles.has(percentile)) {
            throw new Error(`Unsupported LXN percentile "${percentile}" at "${percentilePath}".`);
        }

        if (seenPercentiles.has(percentile)) {
            throw new Error(`Duplicate LXN percentile "${percentile}" at "${percentilePath}".`);
        }

        seenPercentiles.add(percentile);
        return percentile as SupportedLxnPercentile;
    });
}

function normalizeLxnValueEntry(value: unknown, pathLabel: string): LxnValueEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        timestamp: normalizeRequiredFiniteNumber(value.timestamp, `${pathLabel}.timestamp`),
        l10: normalizeNullableFiniteNumber(value.l10, `${pathLabel}.l10`),
        l50: normalizeNullableFiniteNumber(value.l50, `${pathLabel}.l50`),
        l90: normalizeNullableFiniteNumber(value.l90, `${pathLabel}.l90`),
    };
}

function validateLxnValuesInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.lxn_values'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.lxn_values" input to be an object.');
    }

    const startTime = normalizeRequiredFiniteNumber(
        input.startTime,
        'measuring_point.lxn_values.startTime'
    );
    const endTime = normalizeRequiredFiniteNumber(
        input.endTime,
        'measuring_point.lxn_values.endTime'
    );

    if (startTime >= endTime) {
        throw new Error(
            'Expected "measuring_point.lxn_values.startTime" to be lower than "measuring_point.lxn_values.endTime".'
        );
    }

    return {
        measuringPointId: normalizeRequiredId(
            input.measuringPointId,
            'measuring_point.lxn_values.measuringPointId'
        ),
        startTime,
        endTime,
        percentiles: validateLxnPercentiles(
            input.percentiles,
            'measuring_point.lxn_values.percentiles'
        ),
    };
}

function validateLxnValuesResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.lxn_values'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.lxn_values" result to be an object.');
    }

    return {
        values: normalizeArrayEntries(
            result.values,
            'measuring_point.lxn_values.values',
            normalizeLxnValueEntry
        ),
    };
}

function parseValuesLinkValue(value: unknown): readonly LxnValueEntry[] {
    return normalizeArrayEntries(
        value,
        'measuring_point.lxn_values.values',
        normalizeLxnValueEntry
    );
}

function createLxnValueColumns(): readonly RuntimeTabularColumnDescriptor<LxnValueEntry>[] {
    const percentileColumns = SUPPORTED_LXN_PERCENTILES.map(
        (percentile): RuntimeTabularColumnDescriptor<LxnValueEntry> => ({
            kind: 'measurement',
            key: toLxnColumnKey(percentile),
            // The percentile is part of the name, so it overrides the data type's own title.
            label: `L${percentile}`,
            dataType: DATA_TYPE.LXN,
            defaultAlignment: 'right',
            resolveCell: (entry: LxnValueEntry) => entry[toLxnColumnKey(percentile)],
        })
    );

    return [
        {
            kind: 'timestamp',
            key: 'timestamp',
            label: gettext('TIMESTAMP'),
            defaultAlignment: 'left',
            resolveCell: (entry: LxnValueEntry) => entry.timestamp,
        },
        ...percentileColumns,
    ];
}

function buildLxnValuesInput({
    context,
    graphTimeWindow,
}: LxnInputBuilderArgs): RuntimeDataServiceInput<'measuring_point.lxn_values'> {
    if (context) {
        requireSoundMeasuringPointContext(context, 'LXN values');
    }

    return {
        ...buildRequiredMeasuringPointWindowInput({ context, graphTimeWindow }, 'LXN values'),
        percentiles: [...SUPPORTED_LXN_PERCENTILES],
    };
}

export class LxnValuesRuntimeDataService extends RuntimeDataService<'measuring_point.lxn_values'> {
    static readonly serviceId = 'measuring_point.lxn_values' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_LXN_VALUES_LABEL'),
            description: gettext('DATA_SERVICE_LXN_VALUES_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        const valueColumns = createLxnValueColumns();

        return [
            {
                key: 'values',
                label: gettext('DATA_SERVICE_LXN_VALUES_VALUES_LABEL'),
                description: gettext('DATA_SERVICE_LXN_VALUES_VALUES_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Sound],
                path: 'values',
                valueKind: 'tabular',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns: valueColumns,
                parseTableRows: parseValuesLinkValue,
                buildInput: buildLxnValuesInput,
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'measuring_point.lxn_values',
            LxnValueEntry
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateLxnValuesInput;

    validateResult = validateLxnValuesResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point.lxn_values'>) =>
        from(
            this.client.query<MeasuringPointLxnValuesQuery, MeasuringPointLxnValuesQueryVariables>({
                query: MeasuringPointLxnValuesDocument,
                variables: {
                    measuringPointId: toSoundMeasuringPointRelayId(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                    percentiles: input.percentiles,
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(map(({ data }) => validateLxnValuesResult(data?.lxnValues)));
}

export function createLxnValuesService(client: ApolloClient<object>): LxnValuesRuntimeDataService {
    return new LxnValuesRuntimeDataService(client);
}
